import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useAuth } from '../hooks/useAuth'
import { OrganizationSelector } from '../pages/OrganizationSelector'
import { LandingPage } from '../pages/LandingPage'
import { Loader2 } from 'lucide-react'
import { isTokenValid } from '../lib/tokenUtils'

function HomeRoute() {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Show loading spinner while auth context is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated via context, check for valid tokens as fallback
  if (!isAuthenticated && !user) {
    const authToken = localStorage.getItem('authToken');

    // Check if we have valid tokens that haven't expired
    const hasValidAuthToken = authToken && isTokenValid(authToken);

    // Check for any valid organization-specific customer token
    let hasValidCustomerToken = false;
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('customerToken_')) {
        const token = localStorage.getItem(key);
        if (token && isTokenValid(token)) {
          hasValidCustomerToken = true;
          break;
        }
      }
    }

    // If we have valid tokens but user isn't loaded yet, show loading
    // This handles the race condition where tokens exist but context hasn't finished initializing
    if (hasValidAuthToken || hasValidCustomerToken) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600 dark:text-gray-400">Authenticating...</p>
          </div>
        </div>
      );
    }

    // No valid tokens found, show landing page
    return <LandingPage />;
  }

  // For agents/admins, go directly to helpdesk (unified multi-org view)
  const isAgent = user?.roles?.some((r: string) => ['Admin', 'Agent'].includes(r));
  if (isAgent) {
    return <Navigate to="/helpdesk/dashboard" />;
  }

  // For customers or users without agent role, show organization selector
  return <OrganizationSelector />;
}

export const Route = createFileRoute('/')({
  component: HomeRoute,
})